import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeader = (jwt) => ({ headers: { Authorization: `Bearer ${jwt}` } });

const api = {
  // Auth
  requestMagicLink: (email) => axios.post(`${API}/auth/magic-link`, { email }).then(r => r.data),
  verifyMagicLink: (token) => axios.post(`${API}/auth/verify`, { token }).then(r => r.data),
  loginWithPassword: (email, password) => axios.post(`${API}/auth/login`, { email, password }).then(r => r.data),
  setPassword: (jwt, password) => axios.post(`${API}/auth/set-password`, { password }, authHeader(jwt)).then(r => r.data),
  requestPasswordReset: (email) => axios.post(`${API}/auth/request-reset`, { email }).then(r => r.data),
  resetPassword: (token, new_password) => axios.post(`${API}/auth/reset-password`, { token, new_password }).then(r => r.data),
  registerInvite: (data) => axios.post(`${API}/auth/register-invite`, data).then(r => r.data),
  getMe: (jwt) => axios.get(`${API}/auth/me`, authHeader(jwt)).then(r => r.data),

  // Hub Lifecycle
  createHubAuth: (jwt, data) => axios.post(`${API}/hub-lifecycle/create`, data, authHeader(jwt)).then(r => r.data),
  getMyHubs: (jwt) => axios.get(`${API}/hub-lifecycle/my-hubs`, authHeader(jwt)).then(r => r.data),
  getHubStatus: (hubId) => axios.get(`${API}/hub-lifecycle/${hubId}/status`).then(r => r.data),
  createInvite: (jwt, hubId, data) => axios.post(`${API}/hub-lifecycle/${hubId}/invite`, data, authHeader(jwt)).then(r => r.data),
  getInvites: (jwt, hubId) => axios.get(`${API}/hub-lifecycle/${hubId}/invites`, authHeader(jwt)).then(r => r.data),
  getInviteInfo: (token) => axios.get(`${API}/hub-lifecycle/invite-info/${token}`).then(r => r.data),
  sendInviteEmail: (jwt, hubId, token, email) => axios.post(`${API}/hub-lifecycle/${hubId}/send-invite-email`, { token, email }, authHeader(jwt)).then(r => r.data),
  updateLanguage: (jwt, language) => axios.put(`${API}/auth/language`, { language }, authHeader(jwt)).then(r => r.data),
  joinHub: (jwt, data) => axios.post(`${API}/hub-lifecycle/join`, data, authHeader(jwt)).then(r => r.data),
  deployHub: (jwt, hubId) => axios.post(`${API}/hub-lifecycle/${hubId}/deploy`, {}, authHeader(jwt)).then(r => r.data),
  deleteHub: (jwt, hubId) => axios.delete(`${API}/hub-lifecycle/${hubId}`, authHeader(jwt)).then(r => r.data),

  // Hub Templates
  getTemplates: (jwt) => axios.get(`${API}/hub-lifecycle/templates`, authHeader(jwt)).then(r => r.data),
  saveTemplate: (jwt, data) => axios.post(`${API}/hub-lifecycle/templates`, data, authHeader(jwt)).then(r => r.data),
  deleteTemplate: (jwt, templateId) => axios.delete(`${API}/hub-lifecycle/templates/${templateId}`, authHeader(jwt)).then(r => r.data),

  // Data Import
  uploadAndParse: (jwt, hubId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return axios.post(`${API}/data-import/${hubId}/upload`, formData, {
      headers: { Authorization: `Bearer ${jwt}`, "Content-Type": "multipart/form-data" },
    }).then(r => r.data);
  },
  confirmImport: (jwt, hubId, importId) =>
    axios.post(`${API}/data-import/${hubId}/confirm-import/${importId}`, {}, authHeader(jwt)).then(r => r.data),
  getImports: (jwt, hubId) => axios.get(`${API}/data-import/${hubId}/imports`, authHeader(jwt)).then(r => r.data),

  // Hubs
  getHubs: () => axios.get(`${API}/hubs`).then(r => r.data),
  createHub: (data) => axios.post(`${API}/hubs`, data).then(r => r.data),

  // Sites
  getSites: (hubId) => axios.get(`${API}/sites?hub_id=${hubId}`).then(r => r.data),
  createSite: (data) => axios.post(`${API}/sites`, data).then(r => r.data),
  updateSite: (siteId, data) => axios.put(`${API}/sites/${siteId}`, data).then(r => r.data),
  deleteSite: (siteId) => axios.delete(`${API}/sites/${siteId}`).then(r => r.data),
  assignDriverToSite: (siteId, driverName) => axios.put(`${API}/sites/${siteId}/assign-driver?driver_name=${encodeURIComponent(driverName)}`).then(r => r.data),
  unassignDriverFromSite: (siteId, driverName) => axios.put(`${API}/sites/${siteId}/unassign-driver?driver_name=${encodeURIComponent(driverName)}`).then(r => r.data),

  // Machines
  getMachines: (hubId) => axios.get(`${API}/machines?hub_id=${hubId}`).then(r => r.data),
  createMachine: (data) => axios.post(`${API}/machines`, data).then(r => r.data),
  updateMachine: (machineId, data) => axios.put(`${API}/machines/${machineId}`, data).then(r => r.data),
  deleteMachine: (machineId) => axios.delete(`${API}/machines/${machineId}`).then(r => r.data),

  // Drivers
  getDrivers: (hubId) => axios.get(`${API}/drivers?hub_id=${hubId}`).then(r => r.data),
  createDriver: (data) => axios.post(`${API}/drivers`, data).then(r => r.data),
  updateDriver: (name, data) => axios.put(`${API}/drivers/${name}`, data).then(r => r.data),
  deleteDriver: (name, hubId) => axios.delete(`${API}/drivers/${name}?hub_id=${hubId}`).then(r => r.data),
  toggleShift: (name, hubId, action) => axios.post(`${API}/drivers/${name}/shift?hub_id=${hubId}&action=${action}`).then(r => r.data),

  // Requests
  getRequests: (hubId) => axios.get(`${API}/requests?hub_id=${hubId}`).then(r => r.data),
  createRequest: (data) => axios.post(`${API}/requests`, data).then(r => r.data),
  approveRequest: (reqId, driverName) => axios.put(`${API}/requests/${reqId}/approve?driver_name=${driverName}`).then(r => r.data),
  confirmTransport: (reqId) => axios.put(`${API}/requests/${reqId}/confirm-transport`).then(r => r.data),
  confirmDelivery: (data) => axios.post(`${API}/requests/deliver`, data).then(r => r.data),

  // Events
  getEvents: (hubId, limit = 50) => axios.get(`${API}/events?hub_id=${hubId}&limit=${limit}`).then(r => r.data),
  createEvent: (data) => axios.post(`${API}/events`, data).then(r => r.data),

  // Messages
  getMessages: (hubId, user = "") => axios.get(`${API}/messages?hub_id=${hubId}&user=${user}`).then(r => r.data),
  sendMessage: (data) => axios.post(`${API}/messages`, data).then(r => r.data),
  markRead: (msgId) => axios.put(`${API}/messages/${msgId}/read`).then(r => r.data),

  // Feedback
  getFeedback: (hubId) => axios.get(`${API}/feedback?hub_id=${hubId}`).then(r => r.data),
  sendFeedback: (data) => axios.post(`${API}/feedback`, data).then(r => r.data),

  // High Five
  sendHighFive: (data) => axios.post(`${API}/highfive`, data).then(r => r.data),

  // Routes
  getRoutes: (hubId) => axios.get(`${API}/routes/?hub_id=${hubId}`).then(r => r.data),
  createRoute: (data) => axios.post(`${API}/routes/`, data).then(r => r.data),
  updateRoute: (routeId, data) => axios.put(`${API}/routes/${routeId}`, data).then(r => r.data),
  deleteRoute: (routeId) => axios.delete(`${API}/routes/${routeId}`).then(r => r.data),
  completeRoute: (routeId) => axios.put(`${API}/routes/${routeId}/complete`).then(r => r.data),

  // Calendar
  getCalendarEvents: (hubId) => axios.get(`${API}/calendar/?hub_id=${hubId}`).then(r => r.data),
  createCalendarEvent: (data) => axios.post(`${API}/calendar/`, data).then(r => r.data),
  updateCalendarEvent: (eventId, data) => axios.put(`${API}/calendar/${eventId}`, data).then(r => r.data),
  approveLeave: (eventId, status) => axios.put(`${API}/calendar/${eventId}/approve`, { status }).then(r => r.data),
  deleteCalendarEvent: (eventId) => axios.delete(`${API}/calendar/${eventId}`).then(r => r.data),
  uploadToolbox: (formData) => axios.post(`${API}/calendar/upload-toolbox`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then(r => r.data),
  downloadToolbox: (eventId) => axios.get(`${API}/calendar/${eventId}/download`).then(r => r.data),

  // Machine bulk import
  importMachines: (jwt, hubId, siteId, { file, pasteText }) => {
    const fd = new FormData();
    if (file) fd.append("file", file);
    if (pasteText) fd.append("paste_text", pasteText);
    return axios.post(`${API}/data-import/${hubId}/import-machines/${siteId}`, fd, {
      headers: { Authorization: `Bearer ${jwt}`, "Content-Type": "multipart/form-data" },
    }).then(r => r.data);
  },

  // Translate
  translate: (text, targetLang, sourceLang = "en") =>
    axios.post(`${API}/translate`, { text, target_lang: targetLang, source_lang: sourceLang }).then(r => r.data),

  // TTS
  generateTTS: (text, voice = "nova", speed = 1.0) =>
    axios.post(`${API}/tts`, { text, voice, speed }).then(r => r.data),

  // Seed
  seed: () => axios.post(`${API}/seed`).then(r => r.data),
  seedDemoHub: () => axios.post(`${API}/seed-demo-hub`).then(r => r.data),

  // Hub Admin
  getHubMembers: (hubId) => axios.get(`${API}/hub-admin/members?hub_id=${hubId}`).then(r => r.data),
  getAdminNotifications: (hubId) => axios.get(`${API}/hub-admin/notifications?hub_id=${hubId}`).then(r => r.data),
  markNotifRead: (notifId) => axios.put(`${API}/hub-admin/notifications/${notifId}/read`).then(r => r.data),
  exitHub: (data) => axios.post(`${API}/hub-admin/exit-hub`, data).then(r => r.data),
  kickMember: (data) => axios.post(`${API}/hub-admin/kick-member`, data).then(r => r.data),
  updateActivity: (hubId, email) => axios.post(`${API}/hub-admin/update-activity?hub_id=${hubId}&email=${encodeURIComponent(email)}`).then(r => r.data),
  getAnalytics: (hubId) => axios.get(`${API}/hub-admin/analytics?hub_id=${hubId}`).then(r => r.data),
  getReportSchedule: (jwt, hubId) => axios.get(`${API}/hub-admin/report-schedule?hub_id=${hubId}`, authHeader(jwt)).then(r => r.data),
  setReportSchedule: (jwt, data) => axios.post(`${API}/hub-admin/report-schedule`, data, authHeader(jwt)).then(r => r.data),
  sendReportNow: (jwt, hubId) => axios.post(`${API}/hub-admin/send-report-now?hub_id=${hubId}`, {}, authHeader(jwt)).then(r => r.data),

  // AI Assistant
  aiChat: (hubId, message, history = [], language = "en") =>
    axios.post(`${API}/ai/chat`, { hub_id: hubId, message, history, language }).then(r => r.data),

  // Trial
  getTrialStatus: (token) =>
    axios.get(`${API}/auth/trial-status`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.data),
  startTrial: (token) =>
    axios.post(`${API}/auth/start-trial`, {}, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.data),

  // Demo
  seedDemoHub: () =>
    axios.post(`${API}/seed-demo-hub`).then(r => r.data),

  // Activity Logging
  logActivity: (jwt, data) => axios.post(`${API}/activity/log`, data, authHeader(jwt)).then(r => r.data),
  getActivityLog: (jwt, hubId, params = {}) => {
    const q = new URLSearchParams({ hub_id: hubId, ...params });
    return axios.get(`${API}/activity/log?${q}`, authHeader(jwt)).then(r => r.data);
  },
  getActivitySummary: (jwt, hubId) =>
    axios.get(`${API}/activity/summary?hub_id=${hubId}`, authHeader(jwt)).then(r => r.data),

  // PDF Reports
  getReportUrl: (hubId, section = "all") =>
    `${API}/reports/hub/${hubId}/pdf?section=${section}`,

  // GDPR
  exportMyData: (jwt) => axios.get(`${API}/gdpr/my-data`, authHeader(jwt)).then(r => r.data),
  deleteAccount: (jwt, confirm_email) => axios.delete(`${API}/gdpr/account`, { ...authHeader(jwt), data: { confirm_email } }).then(r => r.data),
  recordConsent: (jwt, consent_type, granted) => axios.post(`${API}/gdpr/consent`, { consent_type, granted }, authHeader(jwt)).then(r => r.data),
  getConsents: (jwt) => axios.get(`${API}/gdpr/consents`, authHeader(jwt)).then(r => r.data),
  getGdprPolicy: () => axios.get(`${API}/gdpr/policy-summary`).then(r => r.data),
};

export default api;
